import { useState } from 'react'
import { decideLeave } from '../../api/ai-panel-regenerate'
import { Modal } from '../primitives/Modal'
import { useToast } from '../primitives/Toast'

interface EndorseToDirectorProps {
  leaveId: string
  /** Blackout name when the escalation reason is a blackout overlap. When
   *  omitted, ``reasonText`` drives the modal copy instead (e.g. unpaid). */
  blackoutName?: string
  /** Generic reason copy shown when ``blackoutName`` is absent. */
  reasonText?: string
  /** CTA / title text. Defaults to the blackout wording for back-compat. */
  cta?: string
  invocationId?: string | undefined
  onSuccess: () => void
  disabled?: boolean
}

/**
 * Manager-side action that replaces the bare Approve button when a request
 * cannot be manager-approved and must go to the Exec Director (RFC 007):
 * either it overlaps a blackout (``blackoutName`` set) or it draws unpaid
 * days (``reasonText`` set). The manager writes a justification (required,
 * non-blank — mirrors the apply-leave reason rule: the button stays disabled
 * until it's filled, no min-length counter or inline error) and escalates.
 *
 * On submit: POST /api/v1/leaves/{id}/decide with
 * `decision: 'endorse', justification: <text>` — server transitions the
 * leave PENDING → ESCALATED, stores manager_justification on the leave
 * row, writes audit action `leave.endorsed_with_justification`.
 */
export function EndorseToDirector({
  leaveId,
  blackoutName,
  reasonText,
  cta = 'Endorse to Director',
  invocationId,
  onSuccess,
  disabled = false,
}: EndorseToDirectorProps): JSX.Element {
  const { notify } = useToast()
  const [open, setOpen] = useState(false)
  const [justification, setJustification] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Mirrors the apply-leave reason rule: required and non-blank, nothing more.
  const isEmpty = justification.trim().length === 0

  const handleSubmit = (): void => {
    // The confirm button is disabled while empty, so there's no blank-state
    // error to surface — just guard against a stray call.
    if (isEmpty) return
    setError(null)
    setSubmitting(true)
    void decideLeave(leaveId, {
      decision: 'endorse',
      justification: justification.trim(),
      invocation_id: invocationId,
    })
      .then(() => {
        setOpen(false)
        setJustification('')
        notify('Escalated to the Executive Director', 'success')
        onSuccess()
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Escalation failed')
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <>
      <button
        type="button"
        data-testid="endorse-to-director"
        disabled={disabled}
        onClick={() => setOpen(true)}
        style={{
          padding: '10px 20px',
          background: 'var(--steel-blue)',
          color: 'white',
          border: 'none',
          borderRadius: 6,
          fontSize: 14,
          fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          minHeight: 44,
        }}
      >
        {cta} →
      </button>

      <Modal
        open={open}
        onClose={() => {
          if (!submitting) {
            setOpen(false)
            setError(null)
          }
        }}
        title={cta}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: 'var(--text-2)',
              lineHeight: 1.5,
            }}
          >
            {blackoutName ? (
              <>
                This leave overlaps the blackout window{' '}
                <strong style={{ color: 'var(--text)' }}>{blackoutName}</strong>.
                You can't approve it directly. Write a justification explaining
                why this request should still go ahead, then escalate.
              </>
            ) : (
              reasonText ??
              "You can't approve this request directly. Write a justification for the Executive Director, then escalate."
            )}
          </p>

          <label
            htmlFor="endorse-justification-textarea"
            style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}
          >
            Justification
          </label>
          <textarea
            id="endorse-justification-textarea"
            data-testid="endorse-justification"
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            disabled={submitting}
            rows={5}
            maxLength={2000}
            placeholder="e.g. Business-critical client meeting requires this employee's attendance. Coverage arranged with X for the dates."
            style={{
              padding: 10,
              border: '1px solid var(--border)',
              borderRadius: 8,
              background: 'var(--surface)',
              fontSize: 13,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />

          {error ? (
            <div
              role="alert"
              style={{
                padding: '10px 12px',
                background: 'var(--danger-bg)',
                color: 'var(--danger-fg)',
                border: '1px solid var(--danger-line)',
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                setError(null)
              }}
              disabled={submitting}
              style={{
                padding: '8px 14px',
                background: 'var(--surface)',
                color: 'var(--text-2)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="endorse-confirm"
              onClick={handleSubmit}
              disabled={submitting || isEmpty}
              style={{
                padding: '8px 14px',
                background: submitting || isEmpty ? 'var(--border-strong)' : 'var(--steel-blue)',
                color: 'var(--surface)',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: submitting || isEmpty ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Working…' : cta}
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
